package org.moxultimate.topwithmox.components.cards

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px
import org.moxultimate.topwithmox.theme.FontFamily
import org.moxultimate.topwithmox.utils.ColorUtils.hexToColorRgb

/**
 * ProductLabelType
 * Defines the visual style and display text for each product ranking badge.
 * Colors are stored as hex strings so they can be used in both Kobweb Color
 * modifiers and raw CSS border property strings without a conversion step.
 *
 * @param text Uppercase label shown inside the pill (e.g. "BEST PICK").
 * @param backgroundHex Fill color of the pill.
 * @param borderHex Outline color of the pill.
 * @param textHex Label text color.
 */
enum class ProductLabelType(
    val text: String,
    val backgroundHex: String,
    val borderHex: String,
    val textHex: String,
) {
    BEST_PICK(
        text = "BEST PICK",
        backgroundHex = "#FFA403",
        borderHex = "#FFA403",
        textHex = "#FFFFFF",
    ),
    BEST_DESIGN(
        text = "BEST DESIGN",
        backgroundHex = "#AD1457",
        borderHex = "#AD1457",
        textHex = "#FFFFFF",
    ),
    BALANCED(
        text = "BALANCED",
        backgroundHex = "#6A1B9A",
        borderHex = "#6A1B9A",
        textHex = "#FFFFFF",
    ),
    EDITORS_CHOICE(
        text = "EDITOR'S CHOICE",
        backgroundHex = "#03A9F4",
        borderHex = "#03A9F4",
        textHex = "#FFFFFF",
    ),
    BUDGET_PICK(
        text = "BUDGET PICK",
        backgroundHex = "#4CAF50",
        borderHex = "#4CAF50",
        textHex = "#FFFFFF",
    ),
}

/**
 * ProductLabel
 * Pill-shaped ranking badge intended to sit as an absolute overlay in the
 * top-left corner of a ProductCard hero image.
 * Use [ProductLabelType] to select the variant: BEST_PICK, RUNNER_UP, or BUDGET_PICK.
 *
 * @param type Label variant that controls the color scheme and text.
 * @param modifier Optional outer modifier — caller applies position/offset for overlay use.
 */
@Composable
fun ProductLabel(
    type: ProductLabelType,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .backgroundColor(hexToColorRgb(type.backgroundHex))
            .borderRadius(50.px)
            .padding(leftRight = 12.px, topBottom = 5.px)
            .styleModifier {
                property("border", "1.5px solid ${type.borderHex}")
                property("display", "inline-flex")
                property("align-items", "center")
            },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            text = type.text,
            modifier = Modifier
                .color(hexToColorRgb(type.textHex))
                .fontFamily(FontFamily)
                .fontSize(11.px)
                .fontWeight(FontWeight.Bold)
                .styleModifier { property("letter-spacing", "0.5px") },
        )
    }
}
